import Cookies from 'js-cookie';
import { WlWindow } from 'typings/globals/cordova';
import { isCordova } from 'utils/platform';
import stringToKebabCase from 'utils/textHelpers/stringToKebabCase';
import { AB_TEST_COOKIE_NAME } from '~/constants';

import {
  ActionPointModalButtonClicked,
  AppleSearchAdsInit,
  BookButtonClicked,
  BookingConfirmed,
  BusinessEvent,
  BusinessEventHandler,
  CheckoutPageViewed,
  Click,
  ContactHostFormSent,
  ContactHostFormSentClick,
  ContactHostFormViewed,
  ContactOtherHostsClick,
  ExperiencePageViewed,
  ExperienceReviewUpvoteChange,
  FAQViewed,
  PageView,
  ReadMoreClicked,
  SearchPageViewed,
} from '../../businessLayer';

declare const window: WlWindow;

function addDefaultParameters(inputParams: object, evt: BusinessEvent) {
  const experimentEventData = {};
  evt.experiments?.forEach((exp) => {
    experimentEventData[`experiment_${exp.id}`] = exp.assignedVariant;
  });

  // Track special store front ab test
  experimentEventData['storefront_ab_test'] =
    Cookies.get(AB_TEST_COOKIE_NAME) || 'not_in_test';

  return {
    traffic_type: evt.traffic_type,
    account_is_host: evt.account_is_host,
    account_id: evt.account_id,
    application: isCordova() ? 'app' : 'ue',
    app_platform: ANALYTICS_PLATFORM,
    app_version: withlocals_version,
    ...experimentEventData,
    ...inputParams,
  };
}
const sendEvent = (
  originalEvent: BusinessEvent,
  eventName: string,
  eventData: { props: { [key: string]: any } },
) => {
  if (!window || !window.plausible) {
    return;
  }

  console.log(
    '[PLAUSIBLE] sending event:',
    originalEvent.type,
    eventName,
    eventData,
  );

  const customUProperty = isCordova()
    ? window.location.href.replace('ionic://', 'https://')
    : undefined;

  const data = Object.assign(
    {},
    {
      u: customUProperty,
      props: {
        ...eventData.props,
      },
    },
  );

  return window.plausible(eventName, data);
};

const plausibleHandler: BusinessEventHandler = (evt: BusinessEvent) => {
  if (!window || !window.plausible) {
    return;
  }

  if (evt.type === 'AppleSearchAdsInit') {
    const appleSearchAdsInit = evt as AppleSearchAdsInit;
    const eventData = addDefaultParameters(
      appleSearchAdsInit,
      appleSearchAdsInit,
    );

    return sendEvent(evt, 'apple_search_ads_init', {
      props: {
        ...eventData,
      },
    });
  }

  if (evt.type === 'PageView') {
    const pageView = evt as PageView;
    const eventData = addDefaultParameters(
      {
        page_title: pageView.title,
        page_location: pageView.location,
        page_path: pageView.path,
      },
      pageView,
    );

    return sendEvent(evt, 'pageview', {
      props: {
        ...eventData,
      },
    });
  }

  if (evt.type === 'ContactOtherHostsClick') {
    const contactOtherHostsClick = evt as ContactOtherHostsClick;
    const hostLocation = contactOtherHostsClick?.host?.hostLocations?.[0];
    const eventData = addDefaultParameters(
      {
        experience_id: contactOtherHostsClick?.experience?.id,
        host_id: contactOtherHostsClick?.host?.id,
        source: contactOtherHostsClick?.source,
        enabledForm: contactOtherHostsClick?.enabledForm,
        country: stringToKebabCase(hostLocation?.country),
        city: stringToKebabCase(hostLocation?.area),
        nrOfAdults: contactOtherHostsClick?.nrOfAdults,
        nrOfChildren: contactOtherHostsClick?.nrOfChildren,
        date: contactOtherHostsClick?.date,
        time: contactOtherHostsClick?.time,
      },
      contactOtherHostsClick,
    );

    return sendEvent(evt, 'contact_other_hosts_click', {
      props: {
        ...eventData,
      },
    });
  }

  if (evt.type === 'SearchPageViewed') {
    const event = evt as SearchPageViewed;
    const eventData = addDefaultParameters(
      {
        city: event.city,
        category: event.category,
      },
      event,
    );

    return sendEvent(evt, 'page_view_search', {
      props: {
        ...eventData,
      },
    });
  }

  if (evt.type === 'ExperiencePageViewed') {
    const event = evt as ExperiencePageViewed;
    const eventData = addDefaultParameters(
      {
        experience: event.experience.id,
      },
      event,
    );

    return sendEvent(evt, 'page_view_experience', {
      props: {
        ...eventData,
      },
    });
  }

  if (evt.type === 'FAQViewed') {
    const event = evt as FAQViewed;
    const eventData = addDefaultParameters(
      {
        experience: event.experience.id,
      },
      event,
    );

    return sendEvent(evt, 'view', {
      props: {
        component: 'faq',
        ...eventData,
      },
    });
  }

  if (evt.type === 'BookButtonClicked') {
    const event = evt as BookButtonClicked;
    const eventData = addDefaultParameters(
      {
        experience_id: event?.experience?.id,
        nrOfAdults: event?.nrOfAdults,
        nrOfChildren: event?.nrOfChildren,
        date: event?.date,
        time: event?.time,
        host_id: event?.host?.id,
      },
      event,
    );

    return sendEvent(evt, 'book_button_clicked', {
      props: {
        ...eventData,
      },
    });
  }

  if (evt.type === 'ReadMoreClicked') {
    const event = evt as ReadMoreClicked;
    const eventData = addDefaultParameters(
      {
        experience_id: event?.experience?.id,
      },
      event,
    );

    return sendEvent(evt, 'read_more_clicked', {
      props: {
        ...eventData,
      },
    });
  }

  if (evt.type === 'BookingConfirmed') {
    const event = evt as BookingConfirmed;
    const eventData = addDefaultParameters(event.getEventData(), event);

    return sendEvent(evt, 'checkout_completed', {
      props: {
        ...eventData,
      },
    });
  }

  if (evt.type === 'CheckoutPageViewed') {
    const event = evt as CheckoutPageViewed;
    const eventData = addDefaultParameters(
      {
        option: evt.type,
        experience_id: event?.experience?.id,
      },
      event,
    );

    return sendEvent(evt, 'page_view_checkout', {
      props: {
        ...eventData,
      },
    });
  }

  if (evt.type === 'ActionPointModalButtonClicked') {
    const event = evt as ActionPointModalButtonClicked;
    const eventData = addDefaultParameters(
      {
        option: evt.type,
        pathname: event?.pathname,
      },
      event,
    );

    return sendEvent(evt, 'action_point_modal_button_clicked', {
      props: {
        ...eventData,
      },
    });
  }

  if (evt.type === 'ContactHostFormViewed') {
    const event = evt as ContactHostFormViewed;
    const hostLocation = event?.host?.hostLocations?.[0];
    const eventData = addDefaultParameters(
      {
        experience_id: event?.experience?.id,
        host_id: event?.host?.id,
        source: event?.source,
        enabledForm: event?.enabledForm,
        country: stringToKebabCase(hostLocation?.country),
        city: stringToKebabCase(hostLocation?.area),
      },
      event,
    );

    return sendEvent(evt, 'page_view_contact_host_form', {
      props: {
        ...eventData,
      },
    });
  }

  if (evt.type === 'ContactHostFormSent') {
    const event = evt as ContactHostFormSent;
    const hostLocation = event?.host?.hostLocations?.[0];
    const eventData = addDefaultParameters(
      {
        experience_id: event?.experience?.id,
        host_id: event?.host?.id,
        source: event?.source,
        enabledForm: event?.enabledForm,
        country: stringToKebabCase(hostLocation?.country),
        city: stringToKebabCase(hostLocation?.area),
        nrOfAdults: event?.nrOfAdults,
        nrOfChildren: event?.nrOfChildren,
        date: event?.date,
        time: event?.time,
        duration: event?.duration,
      },
      event,
    );

    return sendEvent(evt, 'contact_host_form_sent', {
      props: {
        ...eventData,
      },
    });
  }

  if (evt.type === 'ContactHostFormSentClick') {
    const event = evt as ContactHostFormSentClick;
    const hostLocation = event?.host?.hostLocations?.[0];
    const eventData = addDefaultParameters(
      {
        experience_id: event?.experience?.id,
        host_id: event?.host?.id,
        source: event?.source,
        enabledForm: event?.enabledForm,
        country: stringToKebabCase(hostLocation?.country),
        city: stringToKebabCase(hostLocation?.area),
        text: event.buttonText,
      },
      event,
    );
    return sendEvent(evt, 'click', {
      props: {
        ...eventData,
      },
    });
  }

  if (evt.type === 'ExperienceReviewUpvoteChange') {
    const event = evt as ExperienceReviewUpvoteChange;
    const eventData = addDefaultParameters(
      {
        experience_id: event?.experience_id,
        review_id: event?.review_id,
        action: event?.action,
      },
      event,
    );
    return sendEvent(evt, 'click', {
      props: {
        ...eventData,
      },
    });
  }

  if (evt.type === 'Click') {
    const event = evt as Click;
    const eventData = addDefaultParameters(
      {
        ...event,
        text: event?.buttonText,
      },
      event,
    );
    return sendEvent(evt, 'click', {
      props: {
        ...eventData,
      },
    });
  }

  console.warn('[PLAUSIBLE] received non-implemented event:', evt);
};

export { plausibleHandler };
